(() => {
  const selector = '[data-am-omnibus]';

  if (window.__amOmnibusDynamicLoaderInstalled) {
    window.amOmnibusScan?.(document);
    return;
  }

  window.__amOmnibusDynamicLoaderInstalled = true;

  const pendingContainers = [];
  const maxConcurrentRequests = window.matchMedia?.('(max-width: 749px)').matches ? 3 : 6;
  let activeRequests = 0;
  let queueContainer = null;

  const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const initContainer = async (container) => {
    if (!container?.matches?.(selector) || !container.isConnected) return;

    const state = container.dataset.amOmnibusState;
    if (state === 'loading' || state === 'loaded') return;

    const productId = toNumber(container.dataset.productId);
    const variantId = toNumber(container.dataset.variantId);
    if (!productId || !variantId) return;

    const attempt = toNumber(container.dataset.amOmnibusAttempts) || 0;
    container.dataset.amOmnibusAttempts = String(attempt + 1);
    container.dataset.amOmnibusState = 'loading';

    const payload = {
      shop: window.Shopify?.shop || container.dataset.shop,
      productId,
      variantId,
      locale: window.Shopify?.locale || container.dataset.locale,
      currency: window.Shopify?.currency || {
        active: container.dataset.currency,
        rate: 1
      },
      country: window.Shopify?.country || container.dataset.country,
      companyId: toNumber(container.dataset.companyId),
      marketId: toNumber(container.dataset.marketId),
      realPrice: (toNumber(container.dataset.realPriceCents) || 0) / 100,
      realComparePrice: (toNumber(container.dataset.realComparePriceCents) || 0) / 100
    };

    try {
      const response = await fetch('/apps/am-omnibus-price', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Omnibus proxy returned ${response.status}`);
      }

      const data = await response.json();

      if (data.lowestPriceHtml != null) {
        container.innerHTML = data.lowestPriceHtml;
      }

      container.dataset.amOmnibusState = 'loaded';
      container.dispatchEvent(new CustomEvent('am:omnibus:loaded', { bubbles: true }));
    } catch (error) {
      container.dataset.amOmnibusState = 'error';

      if (attempt < 1 && container.isConnected) {
        window.setTimeout(() => {
          container.dataset.amOmnibusState = '';
          queueContainer(container);
        }, 1000);
        return;
      }

      console.error('Omnibus Insight dynamic widget:', error);
    }
  };

  const runQueue = () => {
    while (activeRequests < maxConcurrentRequests && pendingContainers.length) {
      const container = pendingContainers.shift();
      if (!container?.isConnected || container.dataset.amOmnibusState === 'loaded') continue;

      container.dataset.amOmnibusState = '';
      activeRequests += 1;
      initContainer(container).finally(() => {
        activeRequests -= 1;
        runQueue();
      });
    }
  };

  queueContainer = (container) => {
    if (!container?.matches?.(selector) || !container.isConnected) return;

    const state = container.dataset.amOmnibusState;
    if (state === 'pending' || state === 'loading' || state === 'loaded') return;

    container.dataset.amOmnibusState = 'pending';
    pendingContainers.push(container);
    runQueue();
  };

  let intersectionObserver = null;
  const observedTargets = new WeakMap();

  const observeContainer = (container) => {
    if (!container?.matches?.(selector) || !container.isConnected) return;

    const state = container.dataset.amOmnibusState;
    if (state === 'loading' || state === 'loaded' || state === 'pending' || state === 'observed') return;

    container.dataset.amOmnibusState = 'observed';

    if (intersectionObserver) {
      const target = container.closest('.grid__item') || container.closest('product-card') || container;
      let containers = observedTargets.get(target);

      if (!containers) {
        containers = new Set();
        observedTargets.set(target, containers);
        intersectionObserver.observe(target);
      }

      containers.add(container);
    } else {
      container.dataset.amOmnibusState = '';
      queueContainer(container);
    }
  };

  const preloadMargin = window.matchMedia?.('(max-width: 749px)').matches
    ? '300px 0px'
    : '800px 0px';

  if ('IntersectionObserver' in window) {
    intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        intersectionObserver.unobserve(entry.target);
        const containers = observedTargets.get(entry.target);
        observedTargets.delete(entry.target);

        if (!containers) return;

        containers.forEach((container) => {
          if (!container.isConnected) return;
          container.dataset.amOmnibusState = '';
          queueContainer(container);
        });
      });
    }, {
      rootMargin: preloadMargin,
      threshold: 0
    });
  }

  const scan = (root) => {
    if (!root) return;

    if (root.matches?.(selector)) {
      observeContainer(root);
    }

    root.querySelectorAll?.(selector).forEach(observeContainer);
  };

  window.amOmnibusInit = observeContainer;
  window.amOmnibusScan = scan;

  const queuedContainers = window.amOmnibusQueue || [];
  window.amOmnibusQueue = [];
  queuedContainers.forEach(observeContainer);

  const start = () => {
    scan(document);

    document.addEventListener('collection:products-updated', (event) => {
      const items = Array.isArray(event.detail?.items) ? event.detail.items : [];

      if (items.length) {
        items.forEach(scan);
      } else {
        scan(event.detail?.root || event.target);
      }
    });

    if (!document.querySelector('.section-main-collection-product-grid')) {
      new MutationObserver((records) => {
        records.forEach((record) => {
          record.addedNodes.forEach((node) => {
            if (node.nodeType === 1) scan(node);
          });
        });
      }).observe(document.getElementById('MainContent') || document.documentElement, {
        childList: true,
        subtree: true
      });
    }

    document.addEventListener('shopify:section:load', (event) => {
      scan(event.target);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
